import { useCallback, useEffect, useReducer } from 'react';

/**
 * The v4 account-menu SURFACE state machine — shared by the board host
 * (`V4BoardCaptureHost`) and the home host (`V4HomeDoor`) so the "exactly one
 * surface at a time" invariant and its transitions are defined ONCE, not forked
 * (thea-web #398 shipped this inline on the board; extracted here when the same
 * nested menu was extended to the homepage/People/Calendar/Gift-Guides shells).
 *
 * It owns ONLY the surface enum + the transition contract + reconciliation. Each
 * host keeps its OWN side effects around these transitions — the board's dwell
 * trigger, dismissal telemetry, board context and force-open stay in
 * `V4BoardCaptureHost`; the home host's board-agnostic capture + local/injected
 * sign-in ownership stay in `V4HomeDoor`. Both consume the same toggle / close /
 * open-sheet / open-sign-in transitions, so behaviour and a11y cannot drift.
 *
 * RECONCILIATION (Codex review of the extension plan): the reducer is independent
 * of live account state, so a surface left open must not outlive the state that
 * justified it. Two rules: collapse to `none` when the user becomes signed in
 * (the anon menu no longer renders, and a stale surface would keep suppressing a
 * host's dwell), and close the capture sheet once capture is OBSERVED-confirmed.
 * Neither reopens a surface — they only ever close one.
 */

export type AccountMenuSurface = 'none' | 'account-menu' | 'capture-sheet' | 'sign-in';

type SurfaceAction =
  | { type: 'TOGGLE_MENU' }
  | { type: 'CLOSE_MENU' }
  | { type: 'OPEN_SHEET' } // menu "Save" (or, on the board, a dwell tick)
  | { type: 'OPEN_SIGN_IN' } // menu or sheet "Sign in"
  | { type: 'CLOSE' }; // a surface closed → back to none

export function accountMenuSurfaceReducer(
  surface: AccountMenuSurface,
  action: SurfaceAction,
): AccountMenuSurface {
  switch (action.type) {
    case 'TOGGLE_MENU':
      return surface === 'account-menu' ? 'none' : 'account-menu';
    case 'CLOSE_MENU':
      return surface === 'account-menu' ? 'none' : surface;
    case 'OPEN_SHEET':
      return 'capture-sheet';
    case 'OPEN_SIGN_IN':
      return 'sign-in';
    case 'CLOSE':
      return 'none';
    default:
      return surface;
  }
}

export interface AccountMenuSurfaceApi {
  /** The single surface currently showing. */
  surface: AccountMenuSurface;
  /** Avatar tap — open the menu, or close it if already open. */
  toggleMenu: () => void;
  /** Esc / outside-click / a state change — close the menu (only if it is open). */
  closeMenu: () => void;
  /** Menu "Save" (or the board dwell) → the capture sheet. */
  openSheet: () => void;
  /** Menu / sheet "Sign in" → the sign-in surface. Hosts fire their own opener
   *  (local modal vs an injected one) around this transition. */
  openSignIn: () => void;
  /** Any surface closed (dismiss / captured / modal onClose) → none. */
  close: () => void;
}

export interface UseAccountMenuSurfaceArgs {
  /** When true, the anon menu is gone — collapse any open surface to `none`. */
  signedIn: boolean;
  /** When the capture WRITE is observed-confirmed, close the capture sheet. */
  captureConfirmed: boolean;
}

export function useAccountMenuSurface({
  signedIn,
  captureConfirmed,
}: UseAccountMenuSurfaceArgs): AccountMenuSurfaceApi {
  const [surface, dispatch] = useReducer(accountMenuSurfaceReducer, 'none');

  // Reconciliation — only ever CLOSES a surface, never opens one.
  useEffect(() => {
    if (signedIn && surface !== 'none') dispatch({ type: 'CLOSE' });
  }, [signedIn, surface]);
  useEffect(() => {
    if (captureConfirmed && surface === 'capture-sheet') dispatch({ type: 'CLOSE' });
  }, [captureConfirmed, surface]);

  const toggleMenu = useCallback(() => dispatch({ type: 'TOGGLE_MENU' }), []);
  const closeMenu = useCallback(() => dispatch({ type: 'CLOSE_MENU' }), []);
  const openSheet = useCallback(() => dispatch({ type: 'OPEN_SHEET' }), []);
  const openSignIn = useCallback(() => dispatch({ type: 'OPEN_SIGN_IN' }), []);
  const close = useCallback(() => dispatch({ type: 'CLOSE' }), []);

  return { surface, toggleMenu, closeMenu, openSheet, openSignIn, close };
}
